import type { CurrentSubscriptionPlan, ProductPlan, OrderedPlan, SubscriptionData, FeatureValueData } from './types';
import { flexProductFamilies } from '../../constants/products';
import { isAnnually } from '../../helpers/flex-helpers/flexBillingFrequency';

export const getPriceSummary = (product?: ProductPlan, currentSubscription?: CurrentSubscriptionPlan): string => {
  const price = currentSubscription?.price ?? product?.price;
  if (!price) return 'Free';
  const term = currentSubscription?.termFrequency ?? product?.term;
  const period = isAnnually(term) ? 'year' : 'month';
  return `£${price} / ${period}`;
};

/** Joins sentence fragments into a single announcement, e.g. "Identity Plus. Boost your score. Included." */
const toAnnouncement = (parts: (string | null | undefined | false)[]): string =>
  `${parts
    .filter(Boolean)
    .map(part => (part as string).trim().replace(/\.$/, ''))
    .join('. ')}.`;

/**
 * Splits a label into everything up to its final word, and that final word, so a caller can
 * keep that word and a trailing inline icon in one `nowrap` span. Without it the icon is its
 * own wrap opportunity and can end up alone on a second line.
 */
export const splitTrailingWord = (label: string): [string, string] => {
  const lastSpace = label.lastIndexOf(' ');
  return lastSpace === -1 ? ['', label] : [label.slice(0, lastSpace + 1), label.slice(lastSpace + 1)];
};

/** "£14.99 / month" reads as "pound 14.99 slash month", so the separator is spoken out. */
const spokenPriceSuffixes = [
  [' / month', ' a month'],
  [' / year', ' a year'],
] as const;

export const getSpokenPrice = (price: string | null): string | null => {
  if (!price) return null;

  const suffix = spokenPriceSuffixes.find(([separator]) => price.endsWith(separator));
  if (!suffix) return price;

  const [separator, spoken] = suffix;
  return `${price.slice(0, -separator.length)}${spoken}`;
};

/** The compare table has no column headers, so each cell names its own plan and feature. */
export const getEntitlementCellAnnouncement = (
  planName: string,
  featureLabel: string,
  value?: FeatureValueData
): string => {
  const included = Boolean(value?.included);
  return toAnnouncement([
    planName,
    featureLabel,
    included ? 'Included' : 'Not included',
    included && (value?.a11yText || value?.text),
  ]);
};

export const getOrderedPlans = (
  allProducts: ProductPlan[],
  subscriptionData: SubscriptionData,
  isTrialEligible?: boolean,
  currentSubscription?: CurrentSubscriptionPlan
): OrderedPlan[] => {
  const currentFamily = currentSubscription?.family ?? '';
  const otherPlans = subscriptionData.plans.filter(p => p.id !== currentFamily);
  const currentPlan = subscriptionData.plans.find(p => p.id === currentFamily);
  const ordered = currentPlan ? [currentPlan, ...otherPlans] : otherPlans;

  return ordered.map(plan => {
    const product = allProducts.find(
      o =>
        o.family === plan.id &&
        (isTrialEligible && o.family !== flexProductFamilies.basic ? o.trialLength === 30 : o.trialLength !== 30)
    );
    const subscriptionForPrice = plan.id === currentFamily ? currentSubscription : undefined;
    const priceSummary = plan.id === 'basic' ? 'Free' : getPriceSummary(product, subscriptionForPrice);

    return {
      id: plan.id,
      family: plan.id,
      tabLabel: plan.tabLabel,
      cardTitle: plan.cardTitle,
      cardSubtitle: plan.cardSubtitle,
      product,
      priceSummary,
      isCurrent: plan.id === currentFamily,
    };
  });
};
